import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, DownloadSimple, CaretDown, CaretRight, ClockCounterClockwise } from '@phosphor-icons/react';
import { formatNzDateTime } from '../utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABELS = {
  small_single_pct: 'Small Single',
  small_box_pct: 'Small Box',
  small_pallet_pct: 'Small Pallet Layer',
  large_single_pct: 'Large Single',
  large_box_pct: 'Large Box',
  large_pallet_pct: 'Large Pallet Layer',
  trial_small_single_pct: 'Trial — Small Single',
  trial_small_box_pct: 'Trial — Small Box',
  trial_small_pallet_pct: 'Trial — Small Pallet Layer',
  trial_large_single_pct: 'Trial — Large Single',
  trial_large_box_pct: 'Trial — Large Box',
  trial_large_pallet_pct: 'Trial — Large Pallet Layer',
};

const flattenStructure = (s, names) => {
  const m = {};
  Object.entries(s?.discountOverrides || {}).forEach(([k, v]) => {
    m[`tier:${k}`] = { label: `Saver tier · ${TIER_LABELS[k] || k}`, value: Number(v) };
  });
  Object.entries(s?.productDiscounts || {}).forEach(([pid, v]) => {
    m[`prod:${pid}`] = { label: `Product · ${names[pid] || pid}`, value: Number(v) };
  });
  const prepay = Number(s?.prepayDiscountPct || 0);
  if (prepay) m.prepay = { label: 'Prepay reward', value: prepay };
  return m;
};

const diffStructures = (curr, prev, names) => {
  const before = flattenStructure(prev, names);
  const after = flattenStructure(curr, names);
  const changes = [];
  Object.keys(after).forEach((k) => {
    if (!(k in before)) changes.push({ kind: 'added', label: after[k].label, to: after[k].value });
    else if (before[k].value !== after[k].value) changes.push({ kind: 'changed', label: after[k].label, from: before[k].value, to: after[k].value });
  });
  Object.keys(before).forEach((k) => {
    if (!(k in after)) changes.push({ kind: 'removed', label: before[k].label, from: before[k].value });
  });
  return changes;
};

const StructureEntries = ({ structure, names }) => {
  const entries = Object.values(flattenStructure(structure, names));
  if (entries.length === 0) {
    return <p className="text-xs text-text-secondary italic">No customer-specific pricing — global defaults apply.</p>;
  }
  return (
    <ul className="space-y-1">
      {entries.map((e) => (
        <li key={e.label} className="flex justify-between gap-3 text-xs">
          <span className="text-text-secondary">{e.label}</span>
          <span className="text-text-primary font-semibold shrink-0">{e.value}%</span>
        </li>
      ))}
    </ul>
  );
};

const DiffLines = ({ changes }) => (
  <ul className="space-y-1">
    {changes.map((c, i) => (
      <li key={`${c.label}-${i}`} className="text-xs flex items-baseline gap-2">
        {c.kind === 'added' && (
          <><span className="text-success font-bold shrink-0">+</span><span className="text-text-primary">{c.label}: set to <span className="text-success font-semibold">{c.to}%</span></span></>
        )}
        {c.kind === 'changed' && (
          <><span className="text-warning font-bold shrink-0">±</span><span className="text-text-primary">{c.label}: <span className="text-text-secondary">{c.from}%</span> → <span className="text-warning font-semibold">{c.to}%</span></span></>
        )}
        {c.kind === 'removed' && (
          <><span className="text-danger font-bold shrink-0">−</span><span className="text-text-primary">{c.label}: <span className="text-danger">removed</span> <span className="text-text-secondary">(was {c.from}%)</span></span></>
        )}
      </li>
    ))}
  </ul>
);

const SnapshotCard = ({ snap, prevSnap, names, index }) => {
  const [expanded, setExpanded] = useState(false);
  const isOldest = !prevSnap;
  const changes = useMemo(
    () => (isOldest ? [] : diffStructures(snap.structure || {}, prevSnap.structure || {}, names)),
    [snap, prevSnap, names, isOldest]
  );
  return (
    <div className="border border-border-color rounded-lg p-4 bg-black/30" data-testid={`discount-snapshot-${index}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <p className="text-sm text-text-primary font-semibold">{formatNzDateTime(snap.capturedAt)}</p>
          <p className="text-xs text-text-secondary">by {snap.changedBy || '—'}</p>
        </div>
        {snap.baseline && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border border-gold/50 text-gold bg-gold/10 shrink-0">
            Baseline
          </span>
        )}
      </div>
      {isOldest ? (
        <div>
          <p className="text-[11px] uppercase tracking-wide text-text-secondary mb-1.5">Starting structure</p>
          <StructureEntries structure={snap.structure} names={names} />
        </div>
      ) : (
        <div>
          <p className="text-[11px] uppercase tracking-wide text-text-secondary mb-1.5">Changes</p>
          {changes.length > 0
            ? <DiffLines changes={changes} />
            : <p className="text-xs text-text-secondary italic">No pricing changes in this save.</p>}
          <button
            onClick={() => setExpanded((e) => !e)}
            data-testid={`snapshot-expand-${index}`}
            className="mt-2 inline-flex items-center gap-1 text-[11px] text-text-secondary hover:text-gold"
          >
            {expanded ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
            Full structure at this point
          </button>
          {expanded && (
            <div className="mt-2 pl-3 border-l border-border-color">
              <StructureEntries structure={snap.structure} names={names} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DiscountHistoryModal = ({ user, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/users/${user.id}/discount-history`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e.message); });
    return () => { cancelled = true; };
  }, [user.id]);

  const names = useMemo(() => {
    const merged = { ...(data?.currentProductNames || {}) };
    (data?.snapshots || []).forEach((s) => Object.assign(merged, s.productNames || {}));
    return merged;
  }, [data]);

  const snapshots = data?.snapshots || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div
        className="bg-surface border border-border-color rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="discount-history-modal"
      >
        <div className="flex items-center justify-between gap-3 p-5 border-b border-border-color">
          <div className="min-w-0">
            <h3 className="text-xl font-bold font-heading text-gold flex items-center gap-2">
              <ClockCounterClockwise size={20} weight="duotone" /> Discount history
            </h3>
            <p className="text-xs text-text-secondary truncate">{user.name} · {user.email}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href={`${API}/users/${user.id}/discount-history/export`}
              data-testid="discount-history-export"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border-color text-text-secondary rounded-lg text-xs hover:border-gold hover:text-gold"
            >
              <DownloadSimple size={14} weight="bold" /> Export CSV
            </a>
            <button onClick={onClose} data-testid="discount-history-close" className="p-1.5 text-text-secondary hover:text-gold" aria-label="Close">
              <X size={18} weight="bold" />
            </button>
          </div>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {error && <p className="text-sm text-danger" data-testid="discount-history-error">{error}</p>}
          {!data && !error && <p className="text-sm text-text-secondary">Loading…</p>}

          {data && (
            <>
              <div className="border border-gold/40 bg-gold/5 rounded-lg p-4" data-testid="discount-history-current">
                <p className="text-[11px] uppercase tracking-wide text-gold mb-1.5">Current structure</p>
                <StructureEntries structure={data.current} names={names} />
              </div>

              {snapshots.length === 0 ? (
                <p className="text-sm text-text-secondary italic" data-testid="discount-history-empty">
                  No changes recorded yet — history starts the first time someone saves this customer&apos;s Overrides.
                </p>
              ) : (
                <div className="space-y-3">
                  <p className="text-[11px] uppercase tracking-wide text-text-secondary">History (newest first)</p>
                  {snapshots.map((s, i) => (
                    <SnapshotCard key={s.id || i} snap={s} prevSnap={snapshots[i + 1]} names={names} index={i} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscountHistoryModal;
